// @flow
import React from 'react';
import styled from 'styled-components';
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { addOnTypeLabels } from 'bk-web-app';
import { colors } from 'bench-style-constants';
import { ConfirmationModal, HighlightKeyword, Icon, Text } from 'bench-internal-theme';
import { openModal } from 'modal-core-components';
import type { Addon } from '../types';
import volumeLabels from '../volumeLabels';
// $FlowFixMe - typescript file
import AddonSidebar from '../addon-sidebar/AddonSidebar';
import { Tooltip } from 'client-web-app/ui';
import TooltipContentPre from '../TooltipContentPre';
import { useDrag } from 'react-dnd';
import { useMediaQuery } from 'react-responsive';
import breakpoints from '../../constants/breakpoints';

const IconWrapper = styled.div`
  position: center;
  text-align: center;
`;

const AddonsTabRowLayout = styled.div`
  align-items: center;
  background-color: ${({ isDragging }) => (isDragging ? colors.sand : colors.white)};
  border-bottom: 1px solid ${colors.silt};
  box-shadow: 0 -1px 0 ${colors.silt};
  display: grid;
  grid-column-gap: 16px;
  margin: 0 32px;
  padding: 8px;
  min-height: 41px;
  position: relative;
  opacity: ${({ isDragging }) => (isDragging ? '.5' : '1')};
  cursor: ${({ isDragging, isSorting, canSort }) => {
    if (isSorting && canSort) {
      return isDragging ? 'grabbing' : 'grab';
    }
    return 'default';
  }};
  opacity: ${({ isSortSaving }) => (isSortSaving ? 0.5 : 1)};
  transition: ${({ isSortSaving }) => (isSortSaving ? 0.5 : 1)};
  grid-template-columns: ${({ isLd, isHd, isTablet }) => {
    if (isHd) {
      return '3fr 0.2fr 1fr 1fr 1fr 0.5fr 0.8fr 0.8fr 0.2fr 0.2fr';
    }
    if (isLd) {
      return `3fr 0.2fr 1fr 1fr 1fr 0.2fr 0.2fr`;
    }
    if (isTablet) {
      return `3fr 2fr 1fr 0.2fr 0.2fr`;
    }
    return `3fr 0.2fr 0.2fr`;
  }};
`;

type AddonsTabRowProps = {
  addon: Addon,
  search: string,
  deleteAddon: () => any,
  isSorting: boolean,
  isSortSaving: boolean,
  canSort: boolean,
};

const AddonsTabRow = ({
  addon,
  search,
  deleteAddon,
  isSorting,
  isSortSaving,
  canSort,
}: AddonsTabRowProps) => {
  const {
    accountType,
    collectionMethod,
    dateActive,
    dateInactive,
    id,
    name,
    optimizationStatus,
    volume,
    details,
  } = addon;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'Add-on',
      item: { id: addon.id },
      canDrag: () => isSorting && canSort,
      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
        canDrag: !!monitor.canDrag(),
      }),
    }),
    [isSorting, canSort],
  );

  const handleDelete = () => {
    openModal(closeModal => (
      <ConfirmationModal
        closeModal={closeModal}
        description={`Would you like to delete ${name}? This action is irreversible.`}
        onConfirm={deleteAddon}
        title="Are you sure?"
      />
    ));
  };

  const isHd = useMediaQuery({ minWidth: breakpoints.hd });
  const isLd = useMediaQuery({ minWidth: breakpoints.ld });
  const isTablet = useMediaQuery({ minWidth: breakpoints.tablet });

  return (
    <AddonsTabRowLayout
      ref={drag}
      isDragging={isDragging}
      data-testid="addon-table-row"
      isSorting={isSorting}
      isSortSaving={isSortSaving}
      canSort={canSort}
      isLd={isLd}
      isHd={isHd}
      isTablet={isTablet}
    >
      <Text size="medium" color={colors.charcoal}>
        <HighlightKeyword keyword={search}>{name}</HighlightKeyword>
      </Text>
      {isTablet && (
        <>
          <IconWrapper>
            {details && (
              <Tooltip
                inlineFlex={false}
                placement="bottom"
                tooltipContent={<TooltipContentPre>{details}</TooltipContentPre>}
                styleType="light"
              >
                <Icon
                  cursor="help"
                  title="Notes"
                  hoverColor={colors.silt}
                  color={colors.dust}
                  icon="notebook"
                  size="small"
                />
              </Tooltip>
            )}
          </IconWrapper>
          <Text styleType="small" color={colors.cloud}>
            {accountType && addOnTypeLabels[accountType]}
          </Text>
        </>
      )}
      {isLd && (
        <>
          <Text styleType="small" color={colors.cloud}>
            {dateActive}
          </Text>
          <Text styleType="small" color={colors.cloud}>
            {dateInactive}
          </Text>
        </>
      )}
      {isHd && (
        <>
          <Text styleType="small" color={colors.cloud}>
            {volume && volumeLabels[volume]}
          </Text>
          <Text styleType="small" color={colors.cloud}>
            {collectionMethod}
          </Text>
          <Text styleType="small" color={colors.cloud}>
            {optimizationStatus}
          </Text>
        </>
      )}
      <AppSidebar>
        {({ closeSidebar, openSidebar }) => (
          <Icon
            cursor="pointer"
            color={colors.cloud}
            hoverColor={colors.silt}
            onClick={() => openSidebar(<AddonSidebar id={id} closeSidebar={closeSidebar} />)}
            icon="pencil"
            aria-label="Edit Add-on"
            size="small"
            title="Edit Add-on"
          />
        )}
      </AppSidebar>
      <Icon
        cursor="pointer"
        color={colors.cloud}
        hoverColor={colors.silt}
        icon="close"
        onClick={handleDelete}
        size="small"
        title="Delete Add-on"
        aria-label="Delete Add-on"
      />
    </AddonsTabRowLayout>
  );
};

export default AddonsTabRow;
